import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarCheck, ArrowRight, Check, Sprout, Landmark, Repeat, ChevronDown, ShieldCheck, FileText, Users } from "lucide-react";
import { SEO } from "@/components/site/SEO";
import { JsonLd, breadcrumbSchema, webPageSchema } from "@/components/site/Schema";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { StickyCta } from "@/components/site/StickyCta";
import { TestimonialCard } from "@/components/site/Testimonials";
import { getTestimonials } from "@/lib/testimonials";
import { Section, Eyebrow, Heading, GatedForm, VideoBlock } from "@/components/site/pageui";
import { AuthorByline } from "@/components/site/AuthorByline";
import family from "@/assets/king-family.jpg";
import legacyCover from "@/assets/res-legacy-outline.png";

// Legacy Builder Map — the nine building blocks organized into three intentional tiers.
const legacyMap = [
  { step: "01", name: "Foundation", icon: Sprout, items: ["Family constitution", "Financial education", "Stewardship and giving"] },
  { step: "02", name: "Structure", icon: Landmark, items: ["Family trust", "Family banking system", "Responsible access to capital"] },
  { step: "03", name: "Continuity", icon: Repeat, items: ["Family meetings", "Entrepreneurship and work", "Long-term continuity"] },
];

const inheritanceIncludes = ["Values and wisdom", "Financial education", "Responsible stewardship", "Opportunity and access", "Family systems and structure"];
const heirs = ["Age-appropriate work", "Financial education", "Family business participation", "Repayment expectations", "Family meetings", "Shared values", "Clear standards", "Mentorship", "Responsible entrepreneurship", "Giving and service"];

// Trust and Legacy Planning — four compact interactive education cards.
const trustCards = [
  {
    key: "incapacity",
    icon: ShieldCheck,
    title: "Continuity During Incapacity",
    value: "Keep the financial system operating.",
    body: [
      "A properly drafted trust may authorize a successor trustee to manage trust-owned assets, pay expenses, and continue carrying out the plan if the person who created the trust can no longer manage those responsibilities.",
      "The actual authority, timing, and responsibilities depend on the trust document and applicable law.",
    ],
  },
  {
    key: "instructions",
    icon: FileText,
    title: "Clear Instructions for the Family",
    value: "Define who benefits, when, and under what guidance.",
    body: [
      "A trust can document how assets should be managed and distributed instead of requiring every beneficiary to receive an unrestricted lump sum immediately.",
      "The terms may reflect the family’s circumstances, responsibilities, timing preferences, and long-term intentions.",
    ],
  },
  {
    key: "transfer",
    icon: Repeat,
    title: "Coordinated Transfer of Assets",
    value: "Create a more organized transition.",
    body: [
      "Assets properly titled to a trust may generally be administered under the trust rather than through probate.",
      "The trust must be properly funded and coordinated with beneficiary designations, account ownership, wills, and the broader estate plan. Assets left outside the trust may still require separate administration.",
    ],
  },
  {
    key: "stewardship",
    icon: Users,
    title: "Stewardship Across Generations",
    value: "Preserve more than ownership.",
    body: [
      "A trust can provide ongoing management, trustee oversight, and distribution instructions intended to support beneficiaries over time.",
      "This may be especially relevant when planning for children, developing heirs, or family members who may not be ready to manage assets independently.",
    ],
  },
];

// Editorial left-border callout (matches Financial Solutions styling).
const Callout = ({ children, light = false }) => (
  <div className={`mt-8 rounded-r-xl border-l-2 border-gold bg-white/55 py-4 pl-5 pr-6 shadow-[0_1px_2px_rgba(17,17,17,0.04)] ${light ? "text-taupe" : "text-slate"}`}>
    <p className="font-serif italic text-base md:text-lg leading-relaxed">{children}</p>
  </div>
);

// Soft branded checklist card used in Trust and Raising Capable Heirs.
const CheckCard = ({ children }) => (
  <div tabIndex={0} className="group flex items-start gap-3 rounded-2xl border border-taupe/45 bg-[#F3ECDD] p-5 outline-none shadow-[0_1px_2px_rgba(17,17,17,0.04)] transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-[0_16px_36px_-26px_rgba(17,17,17,0.5)] focus-visible:-translate-y-1 focus-visible:border-gold focus-visible:shadow-[0_16px_36px_-26px_rgba(17,17,17,0.5)]">
    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold transition-colors group-hover:bg-gold group-hover:text-legacy group-focus-visible:bg-gold group-focus-visible:text-legacy"><Check size={15} strokeWidth={2.5} /></span>
    <span className="pt-0.5 text-slate text-[0.93rem] leading-relaxed">{children}</span>
  </div>
);

// Gentle staggered fade-in reveal on scroll.
const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 22 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.5, delay, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

export default function FamilyLegacy() {
  const testimonials = getTestimonials(["devin", "samuel"]);
  const [openTrust, setOpenTrust] = useState({});
  const toggleTrust = (k) => setOpenTrust((s) => ({ ...s, [k]: !s[k] }));
  return (
    <div className="min-h-screen bg-ivory" data-testid="family-legacy-page">
      <SEO title="Family Banking and Legacy Planning | Legacy Life & Retirement" description="Learn how family banking systems, trusts, financial education, stewardship, and intentional planning can help build generational momentum." />
      <JsonLd id="ld-fl-breadcrumb" data={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Family & Legacy", path: "/family-legacy" }])} />
      <JsonLd id="ld-fl-webpage" data={webPageSchema({ path: "/family-legacy", name: "Family & Legacy", withAuthor: true })} />
      <Header />
      <main>
        {/* 1. Hero — CSS textured warm background + family photo anchor */}
        <section data-testid="hero-section" className="relative overflow-hidden bg-[#E9DFCF] pt-[110px] md:pt-[140px] pb-16 md:pb-24 border-b border-bronze/20">
          {/* Linen / paper texture */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-50 md:opacity-70 bg-[repeating-linear-gradient(0deg,rgba(120,100,70,0.05)_0px,rgba(120,100,70,0.05)_1px,transparent_1px,transparent_4px),repeating-linear-gradient(90deg,rgba(120,100,70,0.04)_0px,rgba(120,100,70,0.04)_1px,transparent_1px,transparent_4px)]" />
          {/* Faint grid overlay */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 grid-lines opacity-[0.16]" />
          {/* Soft Heritage Gold radial glow, upper-left */}
          <div aria-hidden="true" className="pointer-events-none absolute -top-24 left-[-4rem] h-[32rem] w-[32rem] rounded-full bg-[radial-gradient(circle,rgba(199,161,90,0.28),transparent_65%)]" />
          {/* Faint bronze edge vignette */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 shadow-[inset_0_0_150px_rgba(120,90,50,0.16)]" />
          {/* Subtle bottom divider */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
          <div className="container-lr relative z-10 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Eyebrow>Family Purpose. Financial Control. Generational Momentum.</Eyebrow>
              <h1 className="mt-6 font-serif font-semibold tracking-display text-legacy text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">Build a Legacy Your Family Can <span className="accent-word">Live,</span> Not Just Inherit.</h1>
              <p className="mt-7 text-slate text-base md:text-lg leading-relaxed">A meaningful legacy includes more than assets. It includes the values, education, opportunities, habits, and financial systems that allow future generations to begin from a stronger foundation.</p>
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <a href="#legacy-download" className="btn-base btn-primary">Explore the Legacy Builder<ArrowRight size={18} /></a>
                <Link to="/strategy-call" className="btn-base btn-outline-dark"><CalendarCheck size={18} />Book Your Free Strategy Call</Link>
              </div>
              <AuthorByline className="mt-8" />
            </div>
            <div className="relative">
              <div className="absolute -top-4 -left-4 h-20 w-20 border-t border-l border-gold/70" />
              <div className="absolute -bottom-4 -right-4 h-20 w-20 border-b border-r border-gold/70" />
              <img src={family} alt="The King family" loading="lazy" className="rounded-card w-full object-cover shadow-[0_30px_60px_-35px_rgba(17,17,17,0.55)]" />
            </div>
          </div>
        </section>

        {/* 2. More Than Inheritance — distinct ivory block, structured supporting visual */}
        <Section id="inheritance" className="bg-ivory">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <Eyebrow>More Than Inheritance</Eyebrow>
              <Heading>Money Without <span className="accent-word">Wisdom</span> Can Disappear in One Generation.</Heading>
              <p className="mt-6 text-slate text-base leading-relaxed">Wealth is often inherited without the understanding of how it was built, why it was protected, or how it should be used. Assets alone do not prepare the next generation.</p>
              <p className="mt-4 text-slate text-base leading-relaxed">The goal is not entitlement. It is capable, value-driven family members with access to education, opportunity, and responsible financial systems.</p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-card border border-taupe/50 bg-white p-8 md:p-9 shadow-[0_1px_2px_rgba(17,17,17,0.04)]">
                <div className="flex items-center gap-3">
                  <span className="gold-rule" />
                  <h3 className="font-serif font-semibold text-legacy text-2xl md:text-3xl leading-tight">A Legacy Includes</h3>
                </div>
                <ul className="mt-5 divide-y divide-taupe/30">
                  {inheritanceIncludes.map((x) => (
                    <li key={x} className="flex items-center gap-3 py-3.5 text-legacy/90 text-[0.98rem]">
                      <span className="inline-flex h-2 w-2 shrink-0 rounded-full bg-gold" />{x}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </Section>

        {/* 3. Legacy Builder Map / Framework */}
        <Section id="legacy-builder" className="bg-[#EFE8D9]">
          <Reveal>
            <div className="max-w-3xl">
              <Eyebrow>The Legacy Builder Map</Eyebrow>
              <Heading>A Family Legacy Is <span className="accent-word">Built</span>, Not Left to Chance.</Heading>
              <p className="mt-6 text-slate text-base leading-relaxed">Nine building blocks organized into three intentional tiers — a clear path from the values you set today to the momentum future generations carry forward.</p>
            </div>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {legacyMap.map((tier, ti) => {
              const Icon = tier.icon;
              return (
                <Reveal key={tier.name} delay={ti * 0.1}>
                  <div className="lr-card h-full p-7 md:p-8">
                    <div className="flex items-center gap-4">
                      <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-legacy text-gold"><Icon size={22} strokeWidth={1.5} /></span>
                      <div>
                        <span className="font-serif text-2xl text-gold">{tier.step}</span>
                        <h3 className="font-serif text-xl text-legacy leading-tight">{tier.name}</h3>
                      </div>
                    </div>
                    <ul className="mt-6 space-y-3.5 border-t border-gold/25 pt-6">
                      {tier.items.map((item) => (
                        <li key={item} className="flex gap-3 text-slate text-[0.95rem] leading-relaxed">
                          <Check size={17} className="text-gold shrink-0 mt-0.5" />{item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.15}>
            {/* Animated connector — draws the path across the three tiers on scroll (desktop) */}
            <div className="relative mt-12 hidden md:block" aria-hidden="true">
              <motion.span
                className="absolute left-0 top-1/2 h-px w-full origin-left -translate-y-1/2 bg-gradient-to-r from-gold/40 via-gold to-gold/40"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true, margin: "-70px" }}
                transition={{ duration: 1.1, ease: "easeInOut" }}
              />
              <div className="relative flex items-center justify-between">
                {["Foundation", "Structure", "Continuity"].map((n, i) => (
                  <motion.div
                    key={n}
                    className="flex items-center gap-2.5 bg-[#EFE8D9] px-4"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: "-70px" }}
                    transition={{ duration: 0.4, delay: 0.35 + i * 0.35 }}
                  >
                    <span className="inline-flex h-3 w-3 items-center justify-center rounded-full bg-gold ring-4 ring-[#EFE8D9]" />
                    <span className="eyebrow">{n}</span>
                  </motion.div>
                ))}
              </div>
            </div>
            {/* Mobile fallback */}
            <div className="mt-10 flex items-center justify-center gap-3 text-bronze md:hidden">
              <p className="text-sm font-semibold uppercase tracking-[0.18em]">Foundation → Structure → Continuity</p>
            </div>
          </Reveal>
        </Section>

        {/* 4. Family Banking System */}
        <Section id="family-banking" className="bg-ivory">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <Eyebrow>Family Banking System</Eyebrow>
              <Heading>Keep More Financial <span className="accent-word">Momentum</span> Inside the Family.</Heading>
              <p className="mt-6 text-slate text-[0.95rem] leading-relaxed">A family banking system, used responsibly, may support the way capital is stored, accessed, and reused over time for:</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-1">
                {["Vehicles", "Education", "Business opportunities", "Real estate", "Family emergencies", "Travel or major experiences", "Future generations"].map((x) => (
                  <div key={x} className="flex gap-2.5 text-slate text-[0.93rem] py-1.5"><Check size={16} className="text-gold shrink-0 mt-1" />{x}</div>
                ))}
              </div>
              <Callout>Capital must first be created, loans managed responsibly, and clear rules set — so the system builds discipline rather than entitlement, with legal and financial structures coordinated.</Callout>
            </Reveal>
            <Reveal delay={0.1}>
              <VideoBlock id="xJhccj0cHgA" title="How the Wealthy Utilize Whole Life for Legacy Building" />
            </Reveal>
          </div>
        </Section>

        {/* 5. Banking for Children */}
        <Section id="banking-children" className="bg-legacy text-ivory">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <VideoBlock id="M_qoZp92BCc" title="Banking for Children" />
            </Reveal>
            <Reveal delay={0.1}>
              <Eyebrow light>Banking for Children</Eyebrow>
              <Heading light>Begin Before a Child <span className="accent-word">Understands</span> Money.</Heading>
              <ul className="mt-7 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {["Teaching that money has a purpose", "Showing how saving and borrowing work", "Introducing repayment", "Creating long-term capital", "Connecting privileges with responsibility", "Funding opportunity without dependency"].map((x) => (
                  <li key={x} className="flex gap-3 text-taupe text-[0.93rem] leading-relaxed"><Check size={17} className="text-gold shrink-0 mt-0.5" />{x}</li>
                ))}
              </ul>
            </Reveal>
          </div>
        </Section>

        {/* 6. Trust and Legacy Planning */}
        <Section id="trust" className="bg-ivory">
          <Reveal>
            <div className="max-w-3xl">
              <Eyebrow>Trust and Legacy Planning</Eyebrow>
              <Heading>A Trust Can Turn Family <span className="accent-word">Intentions Into Instructions.</span></Heading>
              <p className="mt-6 text-slate text-base leading-relaxed">A trust can create a framework for how certain assets are managed, who may benefit from them, and how family intentions continue if the original owner becomes incapacitated or dies. The appropriate structure depends on the family, the assets, and the purpose of the plan.</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {trustCards.map(({ key, icon: Icon, title, value, body }) => {
                const isOpen = !!openTrust[key];
                const panelId = `trust-panel-${key}`;
                return (
                  <div key={key} className="group h-full rounded-2xl border border-taupe/50 bg-white outline-none transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:bg-legacy hover:shadow-[0_22px_48px_-28px_rgba(17,17,17,0.6)] focus-within:-translate-y-1 focus-within:border-gold focus-within:bg-legacy focus-within:shadow-[0_22px_48px_-28px_rgba(17,17,17,0.6)]">
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => toggleTrust(key)}
                      data-testid={`trust-card-${key}`}
                      className="focus-ring flex w-full flex-col p-6 text-left"
                    >
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gold/15 text-gold transition-colors group-hover:bg-white/10 group-focus-within:bg-white/10"><Icon size={19} strokeWidth={1.6} /></span>
                      <h3 className="mt-4 font-serif text-lg leading-tight text-legacy transition-colors group-hover:text-ivory group-focus-within:text-ivory">{title}</h3>
                      <p className="mt-2 text-[0.9rem] font-semibold leading-snug text-legacy/80 transition-colors group-hover:text-gold group-focus-within:text-gold">{value}</p>
                      <span className="mt-5 inline-flex items-center justify-between gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-bronze transition-colors group-hover:text-gold group-focus-within:text-gold">
                        {isOpen ? "Close Details" : "Click to Learn More"}
                        <ChevronDown size={16} className={`shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                      </span>
                    </button>
                    <div id={panelId} className={`accordion-panel ${isOpen ? "is-open" : ""}`}>
                      <div>
                        <div className="px-6 pb-6">
                          <div className="border-t border-gold/25 pt-4 space-y-3 text-[0.88rem] leading-relaxed text-slate transition-colors group-hover:text-taupe group-focus-within:text-taupe">
                            {body.map((p) => <p key={p}>{p}</p>)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Reality-check callout */}
            <div className="mt-10 rounded-r-xl border-l-2 border-gold bg-[#EFE8D9] py-6 pl-6 pr-7 shadow-[0_1px_2px_rgba(17,17,17,0.05)]">
              <p className="font-serif italic text-lg md:text-xl leading-relaxed text-legacy">A trust is one part of a coordinated estate plan — not an automatic tax strategy, creditor shield, or substitute for proper legal guidance.</p>
              <p className="mt-4 text-slate text-[0.85rem] leading-relaxed">Legacy Life &amp; Retirement provides general education and planning coordination. Trust documents and legal advice must be provided by a qualified estate-planning attorney.</p>
            </div>
          </Reveal>
        </Section>

        {/* 7. Raising Capable Heirs */}
        <Section id="heirs" className="bg-[#EFE8D9]">
          <Reveal>
            <div className="max-w-3xl">
              <Eyebrow>Raising Capable Heirs</Eyebrow>
              <Heading>The Goal Is Opportunity Without <span className="accent-word">Entitlement.</span></Heading>
              <p className="mt-6 text-slate text-base leading-relaxed">Capable heirs are developed intentionally — through work, education, shared values, and clear expectations, not through unearned access.</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-9 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {heirs.map((x) => (
                <CheckCard key={x}>{x}</CheckCard>
              ))}
            </div>
            <Callout>Where employment or trust arrangements are involved, consult qualified tax and legal professionals. This is general education, not tax or legal advice.</Callout>
          </Reveal>
        </Section>

        {/* 8. Legacy Builder Outline download */}
        <Section id="legacy-download" className="bg-ivory">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-stretch">
            <Reveal className="flex">
              <div className="relative w-full">
                <div className="absolute -top-4 -left-4 h-16 w-16 border-t border-l border-gold/70" />
                <div className="absolute -bottom-4 -right-4 h-16 w-16 border-b border-r border-gold/70" />
                <img src={legacyCover} alt="Legacy Builder Outline cover" loading="lazy" className="rounded-card w-full h-full object-contain bg-white p-4 shadow-[0_30px_60px_-30px_rgba(17,17,17,0.5)]" />
              </div>
            </Reveal>
            <Reveal delay={0.1} className="flex">
              <div className="lr-card w-full p-8 md:p-10 flex flex-col justify-center">
                <Eyebrow>Legacy Builder Outline</Eyebrow>
                <Heading>Build Your Family Legacy With <span className="accent-word">Intention.</span></Heading>
                <p className="mt-5 text-slate text-[0.95rem] leading-relaxed">Download the Legacy Builder Outline for a practical framework covering family trusts, financial education, family banking, stewardship, entrepreneurship, and generational momentum.</p>
                <GatedForm list="Legacy Builder Outline" source="family-legacy" />
              </div>
            </Reveal>
          </div>
        </Section>

        {/* 9. Client Experiences */}
        <Section id="experiences" className="bg-[#EFE8D9]">
          <Reveal>
            <div className="max-w-2xl"><Eyebrow>Client Experiences</Eyebrow><Heading>Family impact, in their <span className="accent-word">own words.</span></Heading></div>
          </Reveal>
          <div className="mt-10 grid md:grid-cols-2 gap-6 items-start">
            {testimonials.map((t, i) => (
              <Reveal key={t.id} delay={i * 0.08}><TestimonialCard t={t} index={i} /></Reveal>
            ))}
          </div>
        </Section>

        {/* 10. Final CTA */}
        <Section id="cta" className="bg-ivory">
          <Reveal>
            <div className="relative rounded-card bg-legacy overflow-hidden px-8 py-16 md:px-16 md:py-20 text-center">
              <div className="pointer-events-none absolute -top-px left-1/2 -translate-x-1/2 h-px w-40 bg-gold" />
              <div className="relative max-w-2xl mx-auto">
                <p className="eyebrow text-gold">Begin With Intention</p>
                <Heading light>The Best Time to Build a Family Legacy Is <span className="accent-word">Before It Is Needed.</span></Heading>
                <p className="mt-5 text-taupe text-base md:text-lg leading-relaxed">Begin with the values you want to preserve, the opportunities you want to create, and the financial systems that can help carry both forward.</p>
                <Link to="/strategy-call" data-testid="fl-final-cta" className="btn-base btn-gold mt-9"><CalendarCheck size={18} />Book Your Free Strategy Call</Link>
              </div>
            </div>
          </Reveal>
        </Section>
      </main>
      <Footer />
      <StickyCta />
    </div>
  );
}
